import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useState } from "react";

/* eslint-disable max-lines-per-function */

export interface BulkApprovalResult {
  approved: number;
  failed: Array<{ id: string; title: string; error: string }>;
}

interface BulkApprovalDialogProps {
  readonly isOpen: boolean;
  readonly submissionIds: string[];
  readonly submissions: Array<{ id: string; title: string; posterUrl?: string | null }>;
  readonly onConfirm: (notes: string, publishImmediately: boolean) => Promise<BulkApprovalResult>;
  readonly onCancel: () => void;
  readonly onComplete: () => void;
}

type DialogState = "confirm" | "processing" | "results";

/**
 * Get the dialog title based on current state
 */
function getDialogTitle(state: DialogState): string {
  switch (state) {
    case "processing":
      return "Approving Submissions...";
    case "results":
      return "Bulk Approval Complete";
    default:
      return "Bulk Approve Submissions";
  }
}

/**
 * BulkApprovalDialog - Modal for approving multiple submissions at once
 *
 * Features:
 * - Preview of selected submissions (first 5 with "and X more" indicator)
 * - Shared notes field applied to all approvals
 * - Publish immediately toggle
 * - Progress indicator during bulk operation
 * - Results summary showing successes and failures
 */
export function BulkApprovalDialog({
  isOpen,
  submissionIds,
  submissions,
  onConfirm,
  onCancel,
  onComplete,
}: BulkApprovalDialogProps) {
  const [notes, setNotes] = useState("");
  const [publishImmediately, setPublishImmediately] = useState(false);
  const [dialogState, setDialogState] = useState<DialogState>("confirm");
  const [results, setResults] = useState<BulkApprovalResult | null>(null);

  const selectedSubmissions = submissions.filter((s) => submissionIds.includes(s.id));
  const previewSubmissions = selectedSubmissions.slice(0, 5);
  const remainingCount = selectedSubmissions.length - 5;

  const handleConfirm = async () => {
    setDialogState("processing");
    try {
      const result = await onConfirm(notes, publishImmediately);
      setResults(result);
      setDialogState("results");
    } catch {
      setResults({
        approved: 0,
        failed: selectedSubmissions.map((s) => ({ id: s.id, title: s.title, error: "Network error" })),
      });
      setDialogState("results");
    }
  };

  const handleClose = () => {
    setNotes("");
    setPublishImmediately(false);
    setDialogState("confirm");
    setResults(null);
    if (dialogState === "results") {
      onComplete();
    } else {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
            {dialogState === "processing" ? (
              <Loader2 className="w-6 h-6 text-green-400 animate-spin" />
            ) : (
              <CheckCircle className="w-6 h-6 text-green-400" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-1">{getDialogTitle(dialogState)}</h3>
            <p className="text-sm text-neutral-400">
              {dialogState === "results" && results
                ? `${results.approved} approved, ${results.failed.length} failed`
                : `${submissionIds.length} submissions selected`}
            </p>
          </div>
        </div>

        {/* Confirmation View */}
        {dialogState === "confirm" && (
          <>
            {/* Preview List */}
            <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-3 mb-4">
              <p className="text-sm text-neutral-400 mb-2">Selected submissions:</p>
              <ul className="space-y-2">
                {previewSubmissions.map((submission) => (
                  <li key={submission.id} className="flex items-center gap-2 text-sm text-white">
                    {submission.posterUrl ? (
                      <img src={submission.posterUrl} alt="" className="w-8 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-8 h-12 bg-neutral-700 rounded flex items-center justify-center">
                        <span className="text-xs text-neutral-500">?</span>
                      </div>
                    )}
                    <span className="truncate">{submission.title}</span>
                  </li>
                ))}
              </ul>
              {remainingCount > 0 && <p className="text-sm text-neutral-500 mt-2">...and {remainingCount} more</p>}
            </div>

            {/* Description */}
            <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-green-400">
                All selected providers will be notified that their submissions have been approved.
              </p>
            </div>

            {/* Shared Notes */}
            <div className="mb-4">
              <label htmlFor="bulk-approval-notes" className="text-sm text-neutral-400 block mb-2">
                Shared Admin Notes (optional)
              </label>
              <textarea
                id="bulk-approval-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes that will be included in all approval notifications..."
                rows={3}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none text-sm"
              />
            </div>

            {/* Publish Immediately Toggle */}
            <div className="mb-6">
              <label
                htmlFor="bulk-publish-immediately"
                className="flex items-start gap-3 cursor-pointer"
                aria-label="Publish all immediately"
              >
                <div className="flex items-center h-5">
                  <input
                    id="bulk-publish-immediately"
                    type="checkbox"
                    checked={publishImmediately}
                    onChange={(e) => setPublishImmediately(e.target.checked)}
                    className="w-4 h-4 bg-neutral-800 border-neutral-700 rounded text-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-0"
                  />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-white block">Publish All Immediately</span>
                  <span className="text-xs text-neutral-400 block mt-0.5">
                    Make all approved content available on the platform right away
                  </span>
                </div>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleConfirm();
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              >
                Approve {submissionIds.length} Submissions
              </button>
            </div>
          </>
        )}

        {/* Processing View */}
        {dialogState === "processing" && (
          <div className="text-center py-8">
            <Loader2 className="w-12 h-12 text-green-400 animate-spin mx-auto mb-4" />
            <p className="text-neutral-400">Processing {submissionIds.length} submissions...</p>
            <p className="text-sm text-neutral-500 mt-2">This may take a moment</p>
          </div>
        )}

        {/* Results View */}
        {dialogState === "results" && results && (
          <>
            {/* Success Summary */}
            {results.approved > 0 && (
              <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">
                    {results.approved} submission{results.approved === 1 ? "" : "s"} approved successfully
                  </span>
                </div>
              </div>
            )}

            {/* Failures */}
            {results.failed.length > 0 && (
              <div className="bg-red-600/10 border border-red-600/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">
                    {results.failed.length} submission{results.failed.length === 1 ? "" : "s"} failed
                  </span>
                </div>
                <ul className="space-y-1 text-sm text-neutral-400 max-h-32 overflow-y-auto">
                  {results.failed.map((failure) => (
                    <li key={failure.id} className="flex items-start gap-2">
                      <span className="text-neutral-500">•</span>
                      <span>
                        <span className="text-white">{failure.title}</span>: {failure.error}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Close Button */}
            <button
              type="button"
              onClick={handleClose}
              className="w-full px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors"
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}
